// HR/Finance view of an employee's payout methods, with the ability to ADD one
// ON BEHALF of the employee (created + approved in one step server-side, since the
// caller is already an approver). Employees still add their own via self-service
// (PersonalProfile → Payment Details) and those go through the approval worklist;
// this card is the "the employee gave HR their bank details to enter" path.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Wallet, Plus, Loader2, CheckCircle2, Landmark, Smartphone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { useHasPermission } from '@/rbac/useRbac';
import { getApiErrorMessage } from '@/lib/apiError';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import {
  PaymentMethodChips, PayoutHoldBanner, sortForDisplay, canBeDefault, canDeactivate,
} from '@/features/payment-methods/components/PaymentMethodChips';
import {
  getEmployeePaymentMethods,
  addEmployeePaymentMethod,
  deactivateEmployeePaymentMethod,
  setEmployeeDefaultPaymentMethod,
  type PaymentMethod,
  type PaymentMethodCreate,
  type PaymentMethodType,
} from '@/lib/hrApi';

const MM_PROVIDERS = ['M-Pesa', 'TigoPesa', 'AirtelMoney', 'HaloPesa', 'AzamPesa', 'Other'];
const fieldCls =
  'mt-1 w-full rounded-[10px] border border-border bg-cream px-3 py-2 text-sm text-foreground outline-none focus:border-orange-400';
const labelCls = 'text-xs font-medium text-muted-foreground';

const BLANK: PaymentMethodCreate = {
  type: 'BANK',
  currency: 'TZS',
  country: 'Tanzania',
  bank_name: '',
  branch: '',
  account_holder_name: '',
  account_number: '',
  mobile_money_provider: 'M-Pesa',
  phone_number: '',
};

export const EmployeePayoutMethodsCard: React.FC<{ employeeId: string; employeeName?: string }> = ({
  employeeId,
  employeeName,
}) => {
  const { t } = useTranslation('payroll');
  const confirm = useConfirm();
  const canApprove = useHasPermission('employee:payment:approve');
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  // A failed load is NOT "no payout method yet" — it is shown as an error.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [switching, setSwitching] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState<PaymentMethodCreate>({ ...BLANK });

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setMethods(sortForDisplay(await getEmployeePaymentMethods(employeeId)));
    } catch (e) {
      setMethods([]);
      setLoadError(getApiErrorMessage(e, t('destinations.loadFailed', { defaultValue: 'Could not load payout methods.' })));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  // Switch which APPROVED method payroll pays (one default per employee).
  const makeDefault = async (m: PaymentMethod) => {
    const label = m.type === 'BANK' ? `${m.bank_name ?? ''} ${m.account_masked ?? ''}` : `${m.mobile_money_provider ?? ''} ${m.phone_masked ?? ''}`;
    const ok = await confirm({
      title: t('destinations.switchTitle', { defaultValue: 'Change where salary is paid?' }),
      message: t('destinations.switchBody', {
        defaultValue: 'From the next payroll run, {{name}} will be paid to {{label}}. Runs already locked are not affected.',
        name: employeeName || t('destinations.thisEmployee', { defaultValue: 'this employee' }),
        label: label.trim(),
      }),
      confirmLabel: t('destinations.setDefault', { defaultValue: 'Set as default' }),
    });
    if (!ok) return;
    setSwitching(m.id);
    try {
      await setEmployeeDefaultPaymentMethod(employeeId, m.id);
      toast.success(t('destinations.defaultChanged', { defaultValue: 'Default payment method updated.' }));
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('destinations.defaultFailed', { defaultValue: 'Could not change the default payment method.' })));
    } finally {
      setSwitching(null);
    }
  };

  // PM11: deactivate keeps the record (history) and never redirects salary — if
  // it is the salary destination, payroll holds this employee's pay until a new
  // default is set.
  const deactivate = async (m: PaymentMethod) => {
    const label = m.type === 'BANK' ? `${m.bank_name ?? ''} ${m.account_masked ?? ''}` : `${m.mobile_money_provider ?? ''} ${m.phone_masked ?? ''}`;
    const ok = await confirm({
      title: t('destinations.deactivateTitle', { defaultValue: 'Deactivate this payment method?' }),
      message: t('destinations.deactivateBody', {
        defaultValue:
          '{{label}} will no longer receive salary. It stays in the history. If it is where {{name}} is paid, payroll holds their salary until a new default is set — it is never sent to another account automatically.',
        label: label.trim(),
        name: employeeName || t('destinations.thisEmployee', { defaultValue: 'this employee' }),
      }),
      confirmLabel: t('destinations.deactivate', { defaultValue: 'Deactivate' }),
      danger: true,
    });
    if (!ok) return;
    setSwitching(m.id);
    try {
      await deactivateEmployeePaymentMethod(employeeId, m.id);
      toast.success(t('destinations.deactivated', { defaultValue: 'Payment method deactivated.' }));
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('destinations.deactivateFailed', { defaultValue: 'Could not deactivate the payment method.' })));
    } finally {
      setSwitching(null);
    }
  };

  const setType = (type: PaymentMethodType) => setF((p) => ({ ...p, type }));
  const set = (patch: Partial<PaymentMethodCreate>) => setF((p) => ({ ...p, ...patch }));

  const valid = useMemo(() => {
    if (!f.account_holder_name?.trim()) return false;
    return f.type === 'BANK'
      ? !!f.bank_name?.trim() && !!f.account_number?.trim()
      : !!f.mobile_money_provider && !!f.phone_number?.trim();
  }, [f]);

  const submit = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      // Send only the fields for the chosen type.
      const payload: PaymentMethodCreate =
        f.type === 'BANK'
          ? {
              type: 'BANK',
              currency: f.currency,
              country: f.country,
              bank_name: f.bank_name,
              branch: f.branch || undefined,
              account_holder_name: f.account_holder_name,
              account_number: f.account_number,
              is_primary: methods.length === 0,
            }
          : {
              type: 'MOBILE_MONEY',
              currency: f.currency,
              country: f.country,
              mobile_money_provider: f.mobile_money_provider,
              account_holder_name: f.account_holder_name,
              phone_number: f.phone_number,
              is_primary: methods.length === 0,
            };
      await addEmployeePaymentMethod(employeeId, payload);
      toast.success('Payout method added and approved.');
      setFormOpen(false);
      setF({ ...BLANK });
      await load();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Could not add the payout method.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-orange-100 dark:border-orange-500/20">
      <CardHeader className="rounded-t-xl flex-row items-center gap-2">
        <CardTitle className="flex-1 flex items-center gap-2 text-orange-700">
          <Wallet className="w-5 h-5" /> Payout methods
        </CardTitle>
        {canApprove && !formOpen && (
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white" onClick={() => setFormOpen(true)}>
            <Plus className="w-4 h-4 mr-1" /> Add on behalf
          </Button>
        )}
      </CardHeader>
      <CardContent className="pt-6 space-y-4">
        <p className="text-sm text-muted-foreground">
          Where {employeeName || 'this employee'} is paid. Employees can add their own from their profile (which you
          approve); use <strong>Add on behalf</strong> when they’ve given you their details to enter — it’s approved
          immediately.
        </p>

        {loading ? (
          <div className="py-6 flex items-center justify-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : loadError ? (
          <div role="alert" className="rounded-[10px] bg-danger-soft px-3 py-2 text-sm text-danger-strong">{loadError}</div>
        ) : methods.length === 0 ? (
          <div className="py-4 text-sm text-muted-foreground">No payout method yet.</div>
        ) : (
          <>
          <PayoutHoldBanner methods={methods} />
          <ul className="divide-y divide-border">
            {methods.map((m) => (
              <li key={m.id} className="py-3 flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300">
                  {m.type === 'BANK' ? <Landmark className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-foreground truncate">
                    {m.type === 'BANK' ? m.bank_name : m.mobile_money_provider}{' '}
                    <span className="text-muted-foreground font-normal">{m.account_masked || m.phone_masked}</span>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {m.account_holder_name}
                    {m.branch ? ` · ${m.branch}` : ''}
                    {m.currency ? ` · ${m.currency}` : ''}
                  </div>
                </div>
                <PaymentMethodChips method={m} />
                {canApprove && canBeDefault(m) && (
                  <Button size="sm" variant="outline" disabled={switching !== null} onClick={() => void makeDefault(m)}>
                    {switching === m.id ? <Loader2 className="w-4 h-4 animate-spin" /> : t('destinations.setDefault', { defaultValue: 'Set as default' })}
                  </Button>
                )}
                {canApprove && canDeactivate(m) && (
                  <Button size="sm" variant="outline" disabled={switching !== null} onClick={() => void deactivate(m)}>
                    {t('destinations.deactivate', { defaultValue: 'Deactivate' })}
                  </Button>
                )}
              </li>
            ))}
          </ul>
          </>
        )}

        {canApprove && formOpen && (
          <div className="rounded-2xl border border-orange-200 dark:border-orange-500/30 p-4 space-y-4">
            <div className="flex gap-2">
              {(['BANK', 'MOBILE_MONEY'] as PaymentMethodType[]).map((tp) => (
                <button
                  key={tp}
                  type="button"
                  onClick={() => setType(tp)}
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm ${
                    f.type === tp ? 'bg-orange-500 text-white' : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {tp === 'BANK' ? <Landmark className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
                  {tp === 'BANK' ? 'Bank account' : 'Mobile money'}
                </button>
              ))}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block"><span className={labelCls}>Account holder name</span>
                <input className={fieldCls} value={f.account_holder_name || ''} onChange={(e) => set({ account_holder_name: e.target.value })} /></label>

              {f.type === 'BANK' ? (
                <>
                  <label className="block"><span className={labelCls}>Bank name</span>
                    <input className={fieldCls} value={f.bank_name || ''} onChange={(e) => set({ bank_name: e.target.value })} /></label>
                  <label className="block"><span className={labelCls}>Branch (optional)</span>
                    <input className={fieldCls} value={f.branch || ''} onChange={(e) => set({ branch: e.target.value })} /></label>
                  <label className="block"><span className={labelCls}>Account number</span>
                    <input className={fieldCls} value={f.account_number || ''} onChange={(e) => set({ account_number: e.target.value })} /></label>
                </>
              ) : (
                <>
                  <label className="block"><span className={labelCls}>Provider</span>
                    <select className={fieldCls} value={f.mobile_money_provider} onChange={(e) => set({ mobile_money_provider: e.target.value })}>
                      {MM_PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select></label>
                  <label className="block"><span className={labelCls}>Phone number</span>
                    <input className={fieldCls} placeholder="+255…" value={f.phone_number || ''} onChange={(e) => set({ phone_number: e.target.value })} /></label>
                </>
              )}
              <label className="block"><span className={labelCls}>Currency</span>
                <input className={fieldCls} value={f.currency || 'TZS'} onChange={(e) => set({ currency: e.target.value })} /></label>
            </div>

            <div className="flex items-center gap-2">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={submit} disabled={saving || !valid}>
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Add &amp; approve
              </Button>
              <Button variant="outline" onClick={() => { setFormOpen(false); setF({ ...BLANK }); }} disabled={saving}>Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmployeePayoutMethodsCard;
